import React, { Suspense, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Sky, PerspectiveCamera, Cloud } from '@react-three/drei';
import { FacilityZone } from './3d/VenueDecorations';
import { StructureRenderer } from './3d/StructureRenderer';
import { FacilityMarker } from './3d/FacilityMarker';
import { RouteNavigationPath } from './3d/RouteNavigation';
import { PropRenderer } from './3d/PropRenderer';
import { LandmarkRenderer } from './3d/LandmarkRenderer';
import { VisitorMarker3D } from './3d/VisitorMarker3D';
import { RainEffect } from './3d/effects/RainEffect';
import { GroundPlane } from './3d/effects/GroundPlane';

/**
 * Main outdoor 3D exhibition scene. Composes the camera, lighting,
 * sky/weather effects, ground, booths, facilities, landmarks, route
 * navigation and the visitor GPS marker. All primitives live in
 * `/src/components/3d/` — this file is just a thin composition.
 */
export default function ExhibitionScene3D({
  booths, facilities = [], props: sceneProps = [], landmarks = [],
  onBoothSelect, selectedBooth, weather,
  routeStart, routeEnd, routeActive,
  visitorWorldPos, visitorAccuracy,
}) {
  const isRaining = weather && ['rainy', 'stormy'].includes(weather.condition);
  const isCloudy  = weather && weather.condition === 'cloudy';
  const isNight   = weather && !weather.is_day;

  const skyProps = useMemo(() => {
    if (isRaining) return { turbidity: 20, rayleigh: 4,   inclination: 0.3  };
    if (isCloudy)  return { turbidity: 15, rayleigh: 3,   inclination: 0.4  };
    if (isNight)   return { turbidity: 3,  rayleigh: 0.5, inclination: 0.85 };
    return           { turbidity: 8,  rayleigh: 2,   inclination: 0.5  };
  }, [isRaining, isCloudy, isNight]);

  const highlightZoneTypes = ['prayer_room', 'first_aid', 'toilet', 'parking', 'entrance', 'info'];

  return (
    <Canvas
      shadows
      style={{ width: '100%', height: '100%' }}
      gl={{ antialias: true, powerPreference: 'high-performance' }}
      dpr={[1, 1.5]}
    >
      <PerspectiveCamera makeDefault position={[0, 120, 190]} fov={50} />

      {/* Lighting */}
      <ambientLight intensity={isRaining ? 0.3 : (isCloudy ? 0.4 : 0.5)} />
      <directionalLight
        position={[80, 100, 50]}
        intensity={isRaining ? 0.5 : (isCloudy ? 0.8 : 1.2)}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-far={300}
        shadow-camera-left={-150}
        shadow-camera-right={150}
        shadow-camera-top={150}
        shadow-camera-bottom={-150}
      />
      <hemisphereLight intensity={0.5} groundColor="#4a7c3a" />
      <Sky distance={450000} sunPosition={[100, 40, 100]} azimuth={0.25} {...skyProps} />

      {/* Weather effects */}
      {isRaining && <RainEffect intensity={weather.condition === 'stormy' ? 1000 : 500} />}
      {(isRaining || isCloudy) && (
        <>
          <Cloud position={[-40, 50, -40]} speed={0.3}  opacity={isRaining ? 0.8 : 0.5} scale={30} />
          <Cloud position={[60,  55,  40]} speed={0.2}  opacity={isRaining ? 0.9 : 0.6} scale={25} />
          <Cloud position={[100, 45, -80]} speed={0.15} opacity={isRaining ? 0.7 : 0.4} scale={35} />
        </>
      )}
      <fog attach="fog" args={[isRaining ? '#5a7080' : (isCloudy ? '#8b9dab' : '#a8ccdd'), 250, 500]} />

      {/* Ground + venue plaza */}
      <Suspense fallback={null}>
        <GroundPlane weatherCondition={weather?.condition} />
      </Suspense>

      {/* Booths */}
      <Suspense fallback={null}>
        {booths.map((booth) => (
          <StructureRenderer
            key={booth.id}
            booth={booth}
            onSelect={onBoothSelect}
            isSelected={selectedBooth?.id === booth.id}
            weatherCondition={weather?.condition}
          />
        ))}
      </Suspense>

      {/* Facility markers + prominent zones */}
      <Suspense fallback={null}>
        {facilities.map((facility) => (
          <FacilityMarker key={`fac-${facility.id}`} facility={facility} />
        ))}
        {facilities
          .filter((f) => highlightZoneTypes.includes(f.type))
          .map((f) => (
            <FacilityZone
              key={`zone-${f.id}`}
              position={[f.position_x, 0, f.position_z]}
              type={f.type}
              label={f.name}
              radius={f.type === 'parking' ? 9 : f.type === 'entrance' ? 7 : 5}
            />
          ))}
        <FacilityZone position={[-62, 0, 55]} type="food" label="LAMAN MAKAN & MINUM" radius={10} />
      </Suspense>

      {/* Route navigation line */}
      <RouteNavigationPath start={routeStart} end={routeEnd} active={routeActive} />

      {/* Admin-placed decorations */}
      <Suspense fallback={null}>
        {sceneProps.map((prop) => (
          <PropRenderer key={`prop-${prop.id}`} prop={prop} />
        ))}
      </Suspense>

      {/* Landmarks (stadium, carpark, palms, buildings, archways) */}
      <Suspense fallback={null}>
        {landmarks.map((lm) => (
          <LandmarkRenderer key={`lm-${lm.id}`} landmark={lm} />
        ))}
      </Suspense>

      {/* "You are here" GPS marker */}
      <VisitorMarker3D worldPos={visitorWorldPos} accuracyMeters={visitorAccuracy} />

      <OrbitControls
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        minDistance={30}
        maxDistance={400}
        maxPolarAngle={Math.PI / 2.05}
        target={[0, 0, 0]}
        enableDamping={true}
        dampingFactor={0.05}
      />
    </Canvas>
  );
}
